import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client/react';
import { 
  AppNotification, 
  NotificationType,
  MARK_NOTIFICATION_AS_READ,
  MARK_ALL_NOTIFICATIONS_AS_READ,
  CLEAR_ALL_NOTIFICATIONS,
  LIST_MY_NOTIFICATIONS,
} from '../graphql/notifications';

// Grouped notification for display
interface GroupedNotification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  data: Record<string, any>;
  isRead: boolean;
  createdAt: string;
  count: number;
  notificationIds: string[];
}

function getNotificationIcon(type: NotificationType): string {
  switch (type) {
    case 'MESSAGE': return '💬';
    case 'ASSIGNMENT':
    case 'CLIENT_ASSIGNMENT': return '👤';
    case 'APPOINTMENT': return '📅';
    case 'BULLETIN_POST': return '📰';
    case 'CLIENT_REMINDER': return '⏰';
    case 'TASK': return '✅';
    case 'VIDEO_CONFERENCE': return '📹';
    default: return '🔔';
  }
}

function getNotificationColor(type: NotificationType): string {
  switch (type) {
    case 'MESSAGE': return 'bg-blue-100 text-blue-600';
    case 'ASSIGNMENT':
    case 'CLIENT_ASSIGNMENT': return 'bg-green-100 text-green-600';
    case 'APPOINTMENT': return 'bg-purple-100 text-purple-600';
    case 'BULLETIN_POST': return 'bg-amber-100 text-amber-600';
    case 'CLIENT_REMINDER': return 'bg-red-100 text-red-600';
    case 'TASK': return 'bg-cyan-100 text-cyan-600';
    case 'VIDEO_CONFERENCE': return 'bg-indigo-100 text-indigo-600';
    default: return 'bg-gray-100 text-gray-600';
  }
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}


// Group message notifications by sender - only group UNREAD messages
function groupNotifications(notifications: AppNotification[]): GroupedNotification[] {
  const unreadMessagesBySender = new Map<string, AppNotification[]>();
  const readMessages: AppNotification[] = [];
  const otherNotifications: AppNotification[] = [];

  // Parse data field if it's a string
  const parseData = (data: any): Record<string, any> => {
    if (!data) return {};
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch {
        return {};
      }
    }
    return data;
  };

  for (const notification of notifications) {
    const parsedData = parseData(notification.data);
    const notificationWithParsedData = { ...notification, data: parsedData };
    
    if (notification.type === 'MESSAGE' && parsedData?.senderId) {
      if (!notification.isRead) {
        const senderId = parsedData.senderId;
        if (!unreadMessagesBySender.has(senderId)) {
          unreadMessagesBySender.set(senderId, []);
        }
        unreadMessagesBySender.get(senderId)!.push(notificationWithParsedData);
      } else {
        readMessages.push(notificationWithParsedData);
      }
    } else {
      otherNotifications.push(notificationWithParsedData);
    }
  }

  const grouped: GroupedNotification[] = [];

  // Create grouped notifications for unread messages
  for (const [, messages] of unreadMessagesBySender) {
    messages.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    const mostRecent = messages[0];
    const parsedData = parseData(mostRecent.data);
    const senderName = parsedData?.senderName || 'Someone';
    const count = messages.length;

    const title = count === 1
      ? `New message from ${senderName}`
      : `${count} new messages from ${senderName}`;
    const body = 'Click to view conversation';

    grouped.push({
      id: `grouped-${parsedData?.senderId}`,
      type: 'MESSAGE',
      title,
      body,
      data: parsedData,
      isRead: false,
      createdAt: mostRecent.createdAt,
      count,
      notificationIds: messages.map(m => m.id),
    });
  }

  // Add read messages individually
  for (const notification of readMessages) {
    const parsedData = parseData(notification.data);
    const senderName = parsedData?.senderName || 'Someone';
    grouped.push({
      ...notification,
      data: parsedData,
      title: `Message from ${senderName}`,
      body: 'Click to view conversation',
      count: 1,
      notificationIds: [notification.id],
    });
  }

  // Add other notifications
  for (const notification of otherNotifications) {
    const parsedData = parseData(notification.data);
    grouped.push({
      ...notification,
      data: parsedData,
      count: 1,
      notificationIds: [notification.id],
    });
  }

  // Sort all by createdAt descending
  grouped.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return grouped;
}


interface NotificationsPanelProps {
  notifications: AppNotification[];
  onClose: () => void;
  refetch: () => void;
}

export function NotificationsPanel({ notifications, onClose, refetch }: NotificationsPanelProps) {
  const navigate = useNavigate();
  
  const [markAsRead] = useMutation(MARK_NOTIFICATION_AS_READ, {
    refetchQueries: [{ query: LIST_MY_NOTIFICATIONS, variables: { limit: 50 } }],
  });
  
  const [markAllAsRead] = useMutation(MARK_ALL_NOTIFICATIONS_AS_READ, {
    refetchQueries: [{ query: LIST_MY_NOTIFICATIONS, variables: { limit: 50 } }],
  });
  
  const [clearAll] = useMutation(CLEAR_ALL_NOTIFICATIONS, {
    refetchQueries: [{ query: LIST_MY_NOTIFICATIONS, variables: { limit: 50 } }],
  });

  const groupedNotifications = groupNotifications(notifications);
  const unreadCount = notifications.filter(n => !n.isRead).length;

  const handleNotificationClick = async (notification: GroupedNotification) => {
    // Mark all notifications in this group as read
    try {
      await Promise.all(
        notification.notificationIds.map(id => 
          markAsRead({ variables: { id } })
        )
      );
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }

    onClose();

    const { type, data } = notification;

    // Navigate based on notification type
    switch (type) {
      case 'MESSAGE':
        // Use conversationId if available for direct navigation
        if (data?.conversationId) {
          navigate(`/messages?conversationId=${encodeURIComponent(data.conversationId)}`);
        } else if (data?.senderId) {
          navigate(`/messages?userId=${data.senderId}`);
        } else {
          navigate('/messages');
        }
        break;

      case 'ASSIGNMENT':
        if (data?.assignmentType === 'case' && data?.caseId) {
          navigate(`/cases/${data.caseId}`);
        } else if (data?.assignmentType === 'inquiry' && data?.inquiryId) {
          navigate(`/inquiries/${data.inquiryId}`);
        }
        break;

      case 'APPOINTMENT':
        navigate('/calendar');
        break;

      case 'BULLETIN_POST':
        navigate('/bulletin-board');
        break;

      case 'CLIENT_REMINDER':
        navigate('/clients');
        break;

      case 'TASK':
        navigate('/tasks');
        break;

      case 'VIDEO_CONFERENCE':
        // If joinUrl is available and it's a 'started' notification, open it
        if (data?.joinUrl && data?.notificationType === 'started') {
          window.open(data.joinUrl, '_blank');
        } else if (data?.conferenceId) {
          navigate(`/video-conferences/${data.conferenceId}`);
        } else {
          navigate('/video-conferences');
        }
        break;

      default:
        navigate('/dashboard');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();
      refetch();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleClearAll = async () => {
    try {
      await clearAll();
      refetch();
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  return (
    <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <h3 className="font-semibold text-gray-900">Notifications</h3>
        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="text-sm text-aalaw-red hover:text-aalaw-red/80 font-medium"
            >
              Mark all read
            </button>
          )}
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Notifications List */}
      <div className="max-h-96 overflow-y-auto">
        {groupedNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <svg className="w-12 h-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <p className="font-medium">No Notifications</p>
            <p className="text-sm">You're all caught up!</p>
          </div>
        ) : (
          groupedNotifications.map((notification) => (
            <button
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-50 last:border-b-0 ${
                !notification.isRead ? 'bg-red-50/50' : ''
              }`}
            >
              {/* Icon */}
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg relative ${getNotificationColor(notification.type)}`}>
                {getNotificationIcon(notification.type)}
                {notification.count > 1 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-aalaw-red text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {notification.count}
                  </span>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className={`text-sm truncate ${!notification.isRead ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                    {notification.title}
                  </p>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {formatTimeAgo(notification.createdAt)}
                  </span>
                </div>
                <p className="text-sm text-gray-500 truncate mt-0.5">
                  {notification.body}
                </p>
              </div>

              {/* Unread indicator */}
              {!notification.isRead && (
                <div className="flex-shrink-0 w-2 h-2 bg-aalaw-red rounded-full mt-2" />
              )}
            </button>
          ))
        )}
      </div>

      {/* Footer */}
      {groupedNotifications.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
          <button
            onClick={handleClearAll}
            className="flex items-center justify-center gap-2 w-full text-sm text-red-500 hover:text-red-600 font-medium"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Clear All
          </button>
        </div>
      )}
    </div>
  );
}
